import React, { useEffect, useState } from 'react';
import { AccountPayable, UpdateAccountPayableRequest, Payee, BankAccount, PaymentMethod } from '../types';
import { bankAccountApi, payeeApi, paymentMethodApi } from '../services/api';
import DateInput from './DateInput';

interface AccountPayableFormProps {
    accountPayable: AccountPayable | null;
    onSubmit: (data: UpdateAccountPayableRequest) => Promise<void> | void;
    onCancel: () => void;
    readOnly?: boolean;
}

const AccountPayableForm: React.FC<AccountPayableFormProps> = ({ accountPayable, onSubmit, onCancel, readOnly = false }) => {
    const [payees, setPayees] = useState<Payee[]>([]);
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [showValidation, setShowValidation] = useState(false);

    const [form, setForm] = useState<UpdateAccountPayableRequest>({
        payeeId: accountPayable?.payeeId || '',
        bankAccountId: accountPayable?.bankAccountId || '',
        paymentMethodId: accountPayable?.paymentMethodId || '',
        amount: accountPayable?.amount || 0,
        dueDate: accountPayable?.dueDate || new Date().toISOString(),
        paymentDate: accountPayable?.paymentDate || null,
        recurrence: accountPayable?.recurrence || 'once',
        recurrenceEndDate: accountPayable?.recurrenceEndDate ?? null,
        recurrenceGroupIdentifier: accountPayable?.recurrenceGroupIdentifier ?? null,
        description: accountPayable?.description || '',
    });

    const [amountInput, setAmountInput] = useState<string>('');

    const formatBRL = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    const parseToNumber = (raw: string) => {
        const onlyDigits = raw.replace(/\D/g, '');
        if (!onlyDigits) return 0;
        // interpret as cents of BRL mask input (e.g., 10000 => 100,00)
        const cents = parseInt(onlyDigits, 10);
        return cents / 100;
    };

    useEffect(() => {
        const loadLookups = async () => {
            const results = await Promise.allSettled([
                payeeApi.getAll(),
                bankAccountApi.getAll(),
                paymentMethodApi.getAll(),
            ]);
            const [payeesRes, bankAccountsRes, paymentMethodsRes] = results;
            if (payeesRes.status === 'fulfilled') setPayees(payeesRes.value.data || []);
            if (bankAccountsRes.status === 'fulfilled') setBankAccounts(bankAccountsRes.value.data || []);
            if (paymentMethodsRes.status === 'fulfilled') setPaymentMethods(paymentMethodsRes.value.data || []);
        };
        loadLookups();
    }, []);

    useEffect(() => {
        // initialize masked value from current amount; when editing, backend sends cents
        if (accountPayable) {
            const reais = (accountPayable.amount || 0) / 100;
            setAmountInput(reais ? formatBRL(reais) : '');
            setForm(prev => ({ ...prev, amount: reais } as UpdateAccountPayableRequest));
        } else {
            setAmountInput(form.amount ? formatBRL(form.amount) : '');
        }
    }, []);

    useEffect(() => {
        if (accountPayable) {
            setForm({
                payeeId: accountPayable.payeeId,
                bankAccountId: accountPayable.bankAccountId,
                paymentMethodId: accountPayable.paymentMethodId,
                amount: (accountPayable.amount || 0) / 100,
                dueDate: accountPayable.dueDate,
                paymentDate: accountPayable.paymentDate,
                recurrence: accountPayable.recurrence,
                recurrenceEndDate: accountPayable.recurrenceEndDate ?? null,
                recurrenceGroupIdentifier: accountPayable.recurrenceGroupIdentifier ?? null,
                description: accountPayable.description,
            });
            const reais = (accountPayable.amount || 0) / 100;
            setAmountInput(reais ? formatBRL(reais) : '');
        }
    }, [accountPayable]);

    useEffect(() => {
        const selected = paymentMethods.find(pm => pm.id === form.paymentMethodId);
        setForm(prev => ({ ...prev, bankAccountId: selected ? selected.bankAccountId : '' }));
    }, [form.paymentMethodId, paymentMethods]);

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numberValue = parseToNumber(raw);
        setAmountInput(formatBRL(numberValue));
        // keep form.amount in reais while typing for consistency
        setForm(prev => ({ ...prev, amount: numberValue } as UpdateAccountPayableRequest));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: name === 'amount' ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const reais = parseToNumber(amountInput);
        const cents = Math.round(reais * 100);
        const { recurrence, recurrenceEndDate, ...rest } = form as any;
        const payload = (accountPayable
            ? { ...rest, amount: cents }
            : { ...form, amount: cents }) as UpdateAccountPayableRequest;
        await onSubmit(payload);
    };

    // Validação de campos obrigatórios
    const getValidationErrors = () => {
        const errors: string[] = [];

        if (!form.payeeId) errors.push('Favorecido');
        if (!form.paymentMethodId) errors.push('Método de Pagamento');
        if (!form.bankAccountId) errors.push('Conta Bancária');
        if (parseToNumber(amountInput) <= 0) errors.push('Valor');
        if (!form.dueDate) errors.push('Vencimento');
        if (!form.description.trim()) errors.push('Descrição');
        if (form.recurrence !== 'once' && !form.recurrenceEndDate) errors.push('Fim da Recorrência');

        return errors;
    };

    const isFormValid = () => getValidationErrors().length === 0;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Favorecido *</label>
                    <select name="payeeId" value={form.payeeId} onChange={handleChange} className="input-field" disabled={readOnly} required>
                        <option value="">Selecione</option>
                        {payees.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Método de Pagamento *</label>
                    <select name="paymentMethodId" value={form.paymentMethodId} onChange={handleChange} className="input-field" disabled={readOnly} required>
                        <option value="">Selecione</option>
                        {paymentMethods.map((m) => (
                            <option key={m.id} value={m.id}>{m.description}</option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-secondary-500">
                        {(() => {
                            const selected = paymentMethods.find(pm => pm.id === form.paymentMethodId);
                            const linked = bankAccounts.find(b => b.id === selected?.bankAccountId);
                            if (!selected) return 'Selecione um método para vincular automaticamente a conta bancária.';
                            return linked ? `Conta vinculada: ${linked.name}` : 'Método selecionado sem conta vinculada.';
                        })()}
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Conta Bancária *</label>
                    <select name="bankAccountId" value={form.bankAccountId} onChange={handleChange} className="input-field" disabled required>
                        <option value="">{form.paymentMethodId ? 'Conta vinculada ao método' : 'Selecione um método de pagamento'}</option>
                        {bankAccounts
                            .filter(b => b.id === (paymentMethods.find(m => m.id === form.paymentMethodId)?.bankAccountId))
                            .map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Valor *</label>
                    <input type="text" inputMode="numeric" name="amountMasked" value={amountInput} onChange={handleAmountChange} className="input-field" placeholder="R$ 0,00" disabled={readOnly} required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Vencimento *</label>
                    <DateInput value={form.dueDate} onChange={(v) => setForm({ ...form, dueDate: v ?? form.dueDate })} name="dueDate" disabled={readOnly} required />
                </div>
                {!accountPayable && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Recorrência *</label>
                            <select name="recurrence" value={form.recurrence} onChange={handleChange} className="input-field" required>
                                <option value="once">Única</option>
                                <option value="daily">Diária</option>
                                <option value="weekly">Semanal</option>
                                <option value="monthly">Mensal</option>
                                <option value="yearly">Anual</option>
                            </select>
                        </div>
                        {form.recurrence !== 'once' && (
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">Fim da Recorrência *</label>
                                <DateInput value={form.recurrenceEndDate || null} onChange={(v) => setForm({ ...form, recurrenceEndDate: v })} name="recurrenceEndDate" required />
                            </div>
                        )}
                    </>
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Descrição *</label>
                <textarea name="description" value={form.description} onChange={handleChange} className="input-field" rows={3} disabled={readOnly} required />
            </div>

            {accountPayable && accountPayable.histories && accountPayable.histories.length > 0 && (
                <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold text-secondary-900 mb-3">Histórico</h3>
                    <div className="bg-secondary-50 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-secondary-200">
                            <thead className="bg-secondary-100">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                        Data/Hora
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                                        Descrição
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-secondary-200">
                                {accountPayable.histories
                                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                    .map((history) => (
                                        <tr key={history.id} className="hover:bg-secondary-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900">
                                                {new Date(history.createdAt).toLocaleString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-secondary-900">
                                                {history.description}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showValidation && !isFormValid() && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <p className="font-medium mb-1">Por favor, preencha os seguintes campos obrigatórios:</p>
                    <ul className="list-disc list-inside text-sm">
                        {getValidationErrors().map((error, idx) => (
                            <li key={idx}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary">{readOnly ? 'Fechar' : 'Cancelar'}</button>
                {!readOnly && (
                    <button
                        type="button"
                        onClick={() => {
                            if (isFormValid()) {
                                handleSubmit(new Event('submit') as any);
                            } else {
                                setShowValidation(true);
                            }
                        }}
                        className={`btn-primary ${!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        Salvar
                    </button>
                )}
            </div>
        </form>
    );
};

export default AccountPayableForm;


